import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const StartupPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handlePlatformSelect = (platform) => {
    if (platform === "self") navigate("/register");
    else navigate("/register/organization");
  };

  const platformOptions = [
    {
      id: "org",
      title: "Organization LMS",
      tagline: "Designed for companies and institutes managing structured training.",
      icon: "🏢",
      color: "#0c313f",
      bgColor: "#226c87c7",
      features: [
        "Business Teams (Manager-Employee Hierarchies)",
        "Training Groups for Course Batches & Campaigns",
        "Your own Mentors for Learner Support & Guidance",
        "Advanced Role & Permission Controls",
        "Live Progress Monitoring & Performance Analytics",
        "Skill Gap Analysis & Personality Assessments",
        "Custom Course Builder, Branding & Certificates",
      ],
    },
    {
      id: "self",
      title: "Self Learning Hub",
      tagline: "Perfect for individuals learning at their own pace.",
      icon: "👨‍🎓",
      color: "#1976d2",
      bgColor: "#2d92d1",
      features: [
        "Learn Anytime, Anywhere",
        "Track Your Progress & Assessments",
        "Free Courses Available",
        "Earn Certificates",
        "Personality & Skill Development Tools",
      ],
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        display: "flex",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            fontWeight="bold"
            color="#0c313f"
            sx={{ fontSize: { xs: "2rem", md: "3rem" }, mb: 2 }}
          >
            Welcome to CoreIQPro
          </Typography>
          <Typography
            variant="h6"
            color="#666"
            sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, maxWidth: "600px", mx: "auto" }}
          >
            Choose your learning journey
          </Typography>
        </Box>

        {/* Cards */}
        <Grid container spacing={4} justifyContent="center">
          {platformOptions.map((p) => (
            <Grid item xs={12} md={6} key={p.id}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <CardActionArea onClick={() => handlePlatformSelect(p.id)} sx={{ height: "100%" }}>
                  <CardContent sx={{ textAlign: "center", p: 4 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        backgroundColor: p.bgColor,
                        color: "white",
                        fontSize: "3rem",
                        mb: 3,
                        transition: "0.3s",
                      }}
                    >
                      {p.icon}
                    </Box>

                    <Typography variant="h4" fontWeight="bold" color={p.color} sx={{ mb: 1 }}>
                      {p.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {p.tagline}
                    </Typography>

                    {p.features.map((f, i) => (
                      <Typography key={i} variant="body2" sx={{ color: "#555", mb: 1 }}>
                        ✓ {f}
                      </Typography>
                    ))}

                    <Box
                      sx={{
                        mt: 3,
                        display: "inline-block",
                        px: 2.5,
                        py: 1,
                        borderRadius: "20px",
                        fontWeight: "bold",
                        border: `1px solid ${p.color}`,
                        color: p.color,
                        backgroundColor: "#fff",
                        transition: "0.3s",
                        "&:hover": { backgroundColor: p.color, color: "#fff" },
                      }}
                    >
                      Get Started →
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer Note */}
        <Box textAlign="center" mt={4}>
          <Typography
            variant="body2"
            color="#666"
            sx={{ fontStyle: "italic", maxWidth: "500px", mx: "auto" }}
          >
            Select the platform that best fits your learning needs.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default StartupPage;
